/*
 * Copyright (c) 2020. American Express Company
 */

import $ from 'jquery';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import _ from 'lodash';
import { AccountModel } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import { CategoryModel } from 'src/types/category.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { TransactionSummary } from 'src/types/transaction.model';
import { FullUserModel } from 'src/types/user.model';
import AnimationsService from '../../services/animations/animations.service';
import CardService from '../../services/card.service';
import CashFlowService from '../../services/cashflow.service';
import CategoryService from '../../services/category.service';
import DebtService, { PayDownData } from '../../services/debt.service';
import FinanceService from '../../services/finance.service';
import IncomeSourceService from '../../services/income-source.service';
import NavigationService from '../../services/navigation.service';
import StorageService from '../../services/storage.service';
import TransactionService from '../../services/transaction.service';
import UnitService from '../../services/unit.service';
import ZillowService from '../../services/zillow.service';
import RetireService from '../../services/retire.service';
import UrlHelper from 'src/utils/url.helper';

export default class CardFactory {
    private readonly component: any;
    private loaderTimeout: number;

    constructor(component: any) {
        this.component = component;
    }

    private static isDeletionAttempt($epp: JQuery): boolean {
        return $epp.hasClass('deletion-zone') || $epp.hasClass('deletion-inner') || $epp.hasClass('drop-circle');
    }

    /**
     * When card with a link is updated, also update it's linked card
     * @param updatedCard the origin card that has just been updated
     * @param linkedCard the linked card to also update
     */
    private static async updateLinkedCard(updatedCard: CardModel, linkedCard: CardModel): Promise<any> {
        // TODO move these linked card attributes setters to CardLogic in Node, though maybe not -- since this is a
        // combined dialog (that has both goals), maybe we want to keep this special case code here. Or maybe the
        // dialog can only operate on the BuyHome.attributes, and we'll leave it to the backend to update the savings
        // card.
        switch (updatedCard.description) {
            case 'buy new home':
                const saveForHouseCard: CardModel = linkedCard; // explicitly renamed to keep my head straight

                // multiply by .01 instead of divide by 100 to avoid divide by zero errors if either is set to 0
                // set minimum to $1, because something downstream is ignoring 0 values being sent to it
                saveForHouseCard.attributes.targetBalance = _.max([1, _.get(updatedCard.attributes, 'down_payment')]);
                if (_.get(updatedCard, 'attributes.zillow.zpid')) {
                    saveForHouseCard.attributes = saveForHouseCard.attributes || {};
                    saveForHouseCard.attributes.zillow = updatedCard.attributes.zillow;
                }

                await CardService.moveCardBeforeAnother(updatedCard, saveForHouseCard);

                const months = saveForHouseCard.startDate.until(updatedCard.startDate, ChronoUnit.MONTHS);
                saveForHouseCard.attributes.perMonth = saveForHouseCard.attributes.targetBalance / months;
                await CardService.updateCard(saveForHouseCard);
                break;

            case 'save for home':
                const buyNewHomeCard: CardModel = linkedCard; // explicitly renamed to keep my head straight
                _.set(buyNewHomeCard.attributes, 'down_payment', updatedCard.attributes.targetBalance);
                const purchasePrice: number = _.get(buyNewHomeCard.attributes, 'purchase_price');

                // too cautious, but was otherwise able to break this in the UI
                if (purchasePrice > 0) {
                    _.set(
                        buyNewHomeCard.attributes,
                        'down_payment_percent',
                        (updatedCard.attributes.targetBalance / purchasePrice) * 100
                    );
                } else {
                    _.set(buyNewHomeCard.attributes, 'down_payment_percent', 0);
                }
                await CardService.moveCardAfterAnother(updatedCard, buyNewHomeCard);
                await CardService.updateCard(buyNewHomeCard);

                buyNewHomeCard.attributes.transactionAmount = (buyNewHomeCard.attributes as any).down_payment;

                break;
            case 'take a vacation':
                const saveForVacationCard: CardModel = linkedCard;
                saveForVacationCard.attributes.targetBalance = -_.get(updatedCard.attributes, 'totalAmount');
                await CardService.moveCardBeforeAnother(updatedCard, saveForVacationCard);
                await CardService.updateCard(saveForVacationCard);
                break;
        }
    }

    public async handleCreateAndOpenCard(data: any, startDate: LocalDate, open: boolean = true): Promise<void> {
        const card: CardModel = await this.createCard(data, startDate, true);
        await this.component.handleReloadUser();
        if (open) {
            await this.handleOpenCard(card);
        }
    }

    public async handleCreateAndOpenCardFromRoute(
        data: any,
        startDate: LocalDate,
        open: boolean = true
    ): Promise<void> {
        const card: CardModel = await this.createCard(data, startDate, true);
        await this.component.handleReloadUser();

        if (open) {
            UrlHelper.replacePath(`card/${card.id}`);
        }
    }

    /**
     * Does this card end a job?
     * It does if it is modifying an income and setting the new income to zero
     * @param card to check
     */
    public static isJobEndingCard(card: CardModel): boolean {
        return card.modifyIncomes.length > 0 && card.attributes.newAnnualIncome === 0;
    }

    public async createCard(data: any, startDate: LocalDate, manual: boolean, yPosition?: number): Promise<CardModel> {
        if (!startDate) startDate = LocalDate.now().plusYears(2);
        if (!_.isNumber(yPosition)) yPosition = Math.random();

        let endDate: LocalDate = startDate;

        // TODO this is never set, but I don't know what it was doing
        // let linkedCard: CardModel = null;

        // TODO: make Goal data meaningful in bw-node
        let targetBalance: number = 0;
        let newIncome: number = 0;
        let currentIncome: number = 0;
        let attributes: any = null;
        let currentIncomeSource: IncomeSourceModel;

        // TODO make 225k, calculate 105% of starting salary
        switch (data.description) {
            case 'new job': {
                const incomeSources: IncomeSourceModel[] = await RetireService.getIncomeSources(startDate);
                const largestSource: IncomeSourceModel = incomeSources[0];
                newIncome = largestSource ? largestSource.annualizedIncome * 1.15 : 200000;
                attributes = {
                    newAnnualIncome: data.newIncome || newIncome
                };
                break;
            }
            case 'get promoted':
                const incomeSources: IncomeSourceModel[] = await RetireService.getIncomeSources(startDate);
                const largestSource: IncomeSourceModel = incomeSources[0];
                newIncome = largestSource ? largestSource.annualizedIncome * 1.15 : 200000;
                attributes = {
                    newAnnualIncome: data.newIncome || newIncome
                };
                break;
            case 'quit job':
                newIncome = null;
                // if we are setting a default, we find the first matching job
                if (data.setDefault) {
                    const possibleIncomeSources: IncomeSourceModel[] = await RetireService.getNonEndingIncomeSources(
                        this.component.props.user,
                        startDate
                    );
                    currentIncomeSource = possibleIncomeSources.length >= 1 ? possibleIncomeSources[0] : null;
                }
                attributes = {
                    newAnnualIncome: 0
                };
                break;
            case 'retire':
                newIncome = null;

                const possibleIncomeSources: IncomeSourceModel[] = await RetireService.getNonEndingIncomeSources(
                    this.component.props.user,
                    startDate
                );
                // if we are setting a default, find the first matching. If there's only one, then always use that
                if (possibleIncomeSources.length === 1 || (possibleIncomeSources.length > 1 && data.setDefault)) {
                    currentIncomeSource = possibleIncomeSources[0];
                }
                attributes = {};
                break;
            case 'collect social security':
                currentIncome = await this.component.getIncome();
                const annualIncome = currentIncome ? Math.round(currentIncome) : 0;
                const resp = await FinanceService.retrieveSocialSecurity(startDate, annualIncome);
                const estimatedSocial = annualIncome ? resp.estimated : 0;
                attributes = {
                    average_income: annualIncome,
                    estimate_social: estimatedSocial,
                    newAnnualIncome: estimatedSocial * 12
                };
                break;
            case 'buy new home':
                attributes = {
                    down_payment_percent: 20,
                    purchase_price: 500000,
                    renovation_budget: 0,
                    down_payment: 100000,
                    mortgage_type: 360,
                    mortgage_interest: 3.85,
                    property_tax: 0,
                    property_insurance: 0,
                    borrowing: 0,
                    monthly_payment: 0
                };
                break;
            case 'wedding':
                attributes = {
                    totalAmount: -10000
                };
                break;
            case 'take a vacation':
                attributes = {
                    totalAmount: -2000
                };
                break;
            case 'make a large purchase':
                attributes = {
                    totalAmount: 0
                };
                break;
            case 'donate a portion of your income':
                attributes = {
                    percentage: 10
                };
                endDate = startDate.plusMonths(12);
                break;
            case 'child starts college':
                endDate = startDate.plusMonths(48);
                attributes = {
                    totalAmount: -120000
                };
                break;
            case 'plan for education costs':
                endDate = startDate.plusYears(4);
                attributes = {
                    financialAid: 50000,
                    roomBoard: 50000,
                    isPublic: 'true',
                    inState: 'true'
                };
                break;
            case 'estimate life insurance':
                endDate = startDate;
                const age = this.component.props.user.dob
                    ? this.component.props.user.dob.until(startDate, ChronoUnit.YEARS)
                    : 40;

                attributes = {
                    termLength: 20,
                    age,
                    coverageAmount: 750000,
                    annualPremium: 600
                };
                break;
            case 'have a baby':
                // TODO this is 18 years and 1 month so that it ends on the first of the month after 18 years
                // We will fix this on the backend so that end-dates will be inclusive and ignore the day-of-month
                endDate = startDate.plusMonths(217);
                attributes = {
                    totalAmount: -250000
                };
                break;
            case 'save for home':
                targetBalance = 200000;
                endDate = startDate.plusMonths(30);
                attributes = {
                    targetBalance
                };
                break;
            case 'save for college':
                targetBalance = 120000;
                endDate = startDate.plusMonths(192);
                attributes = {
                    targetBalance
                };
                break;
            // Note -- these should only be created by calling createSavingsGoal with a 'take a vacation' card
            // case 'save for vacation':
            //     targetBalance = 2000;
            //     endDate = startDate.plusMonths(6);
            //     attributes = {
            //         targetBalance: targetBalance
            //     };
            //     break;
            case 'save for wedding':
                targetBalance = 10000;
                endDate = startDate.plusMonths(12);
                attributes = {
                    targetBalance
                };
                break;
            case 'save for baby':
                targetBalance = 10000;
                endDate = startDate.plusMonths(12);
                attributes = {
                    targetBalance
                };
                break;
            case 'save emergency fund':
                currentIncome = await this.component.getIncome();
                targetBalance = Math.round(currentIncome / 4);
                endDate = startDate.plusMonths(24);
                attributes = {
                    targetBalance
                };
                break;
            case 'budget expenses':
                endDate = startDate.plusYears(10);
                break;
            case 'adjust expenses':
                endDate = startDate.plusYears(10);

                attributes = {
                    updated: true,
                    expenseChange: 0
                };
                break;
            case 'lose job':
                newIncome = null;
                attributes = {
                    newAnnualIncome: 0
                };
                break;
            case 'windfall':
                attributes = {
                    transactionAmount: 50000
                };
                break;
            case 'receive inheritance':
                attributes = {
                    transactionAmount: 50000
                };
                break;
            case 'win lottery':
                attributes = {
                    transactionAmount: 18000
                };
                break;
            case 'family member gets sick':
                attributes = {
                    totalAmount: -18000
                };
                endDate = startDate.plusMonths(9);
                break;
            case 'get sick':
                attributes = {
                    totalAmount: -9000,
                    newAnnualIncome: 0,
                    changeIsTemporary: true
                };
                endDate = startDate.plusMonths(6);
                break;
            case 'care for a family member':
                endDate = startDate.plusMonths(12);
                break;
        }

        const card: CardModel = await CardService.createCard(
            {
                description: data.description,
                startDate,
                endDate,
                attributes,
                accountId: null,
                startIncomes: [],
                modifyIncomes: currentIncomeSource ? [currentIncomeSource.incomeUid] : [],
                yPosition
            },
            this.component.props.user,
            manual,
            false
        );

        this.component.mentorFactory.triggerCreateGoal(card);

        // TODO linkedCard is never set... it's always null. Is this here for a reason?
        // if (linkedCard) {
        //     linkedCard.attributes.linked_card_id = card.id;
        //     await CardService.updateCard(linkedCard);
        // }

        return card;
    }

    public handleChangeCard(card: CardModel, value: any, name: string, type: string = 'none'): void {
        const updatedCard: CardModel = { ...card };

        if (name === 'modifyIncomes') {
            updatedCard.modifyIncomes[0] = value;
        } else if (name === 'startIncomes') {
            updatedCard.startIncomes[0] = value;
        } else if (type === 'currency') {
            (updatedCard as any)[name] = Number(value.replace(/[$,]/g, ''));
        } else {
            switch (name) {
                case 'nickname':
                    updatedCard.nickname = String(value);
                    break;
                case 'accountId':
                    if (!value) {
                        (updatedCard as any)[name] = null;
                    } else {
                        (updatedCard as any)[name] = Number(value);
                    }
                    break;
                case 'investmentIds':
                    (updatedCard as any)[name] = value;
                    break;
                default:
                    (updatedCard as any)[name] = Number(value);
                    break;
            }
        }

        if (updatedCard.id === this.component.state.drawerProps.card.id) {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                card: updatedCard
            });
        }
    }

    public handleChangeCardAttribute(
        card: CardModel,
        value: any,
        name: string,
        type: string = 'none',
        user: FullUserModel = null
    ): void {
        const updatedCard: CardModel = { ...card };
        let newVal;
        let hasValue = value !== undefined;
        switch (type) {
            case 'currency':
                newVal = Number(String(value).replace(/[$,]/g, ''));
                break;

            case 'number':
                newVal = Number(String(value));
                break;

            default:
                newVal = value;
                hasValue = value;
        }

        updatedCard.attributes = {
            ...updatedCard.attributes
        };
        if (hasValue) {
            _.set(updatedCard.attributes, name, newVal);
        } else {
            delete updatedCard.attributes[name];
        }

        if (updatedCard.id === this.component.state.drawerProps.card.id) {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                card: updatedCard
            });
        } else if (user) {
            // if our updatedCard is not the card in drawerProps, we have a linked card, so update it in user.cards
            const newCards = _.map(user.cards, (card) => {
                return card.id === updatedCard.id ? updatedCard : card;
            });
            const newUser = user;
            newUser.cards = newCards;
            this.component.handleReloadUser().then(this.component.setState({ user: newUser }));
        }
    }

    public handleChangeCardAttributes(card: CardModel, attributes: any): void {
        const updatedCard: CardModel = { ...card };

        updatedCard.attributes = attributes;

        if (updatedCard.id === this.component.state.drawerProps.card.id) {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                card: updatedCard
            });
        }
    }

    public handleChangeCardDate(date: LocalDate, name: string): void {
        const updatedCard: CardModel = { ...this.component.state.drawerProps.card };

        if (name === 'startDate' && !CardService.isSavingsGoal(updatedCard)) {
            const offset = updatedCard.startDate.until(date, ChronoUnit.DAYS);
            updatedCard.endDate = updatedCard.endDate.plusDays(offset);
        }
        (updatedCard as any)[name] = date;

        if (updatedCard.id === this.component.state.drawerProps.card.id) {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                card: updatedCard
            });
        }
    }

    public handleChangeCardValue(value: any, name: string): void {
        const updatedCard: CardModel = { ...this.component.state.drawerProps.card };

        (updatedCard as any)[name] = value;

        if (updatedCard.id === this.component.state.drawerProps.card.id) {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                card: updatedCard
            });
        }
    }

    public async handleDeleteCard(card: CardModel, handleTopDrawer: boolean = true): Promise<void> {
        await CardService.deleteCard(card);
        this.component.setState({
            incomeSources: null
        });

        await this.component.handleReloadUser();

        if (handleTopDrawer) {
            this.component.drawerFactory.handleTopDrawer();
        }
    }

    public async handleCreateLinkedSavingsCard(card: CardModel): Promise<void> {
        const savingsCard: CardModel = await CardService.createLinkedSavingsCard(card);
        _.set(card, 'attributes.linked_card_id', _.get(savingsCard, 'id'));

        await this.component.handleReloadUser();
    }

    public async handleDeleteLinkedSavingsCard(card: CardModel): Promise<void> {
        await CardService.deleteLinkedSavingsCard(card);
        _.set(card, 'attributes.linked_card_id', undefined);

        await this.component.handleReloadUser();
    }

    public handleOpenLinkedCard(card: CardModel): void {
        const linkedCardId = card.attributes.linked_card_id;
        if (linkedCardId) {
            const linkedCard = _.find(this.component.props.user.cards, { id: linkedCardId });
            if (linkedCard) {
                void this.handleOpenCard(linkedCard);
            }
        }
    }

    public async handleOpenCard(card: CardModel): Promise<any> {
        this.component.goToCard(card.id);
    }

    public async handleOpenCardFromRoute(card: CardModel): Promise<void> {
        const cardData: { card: CardModel; cardIndex: number } = StorageService.getCard(card.id);
        let accounts: AccountModel[] = null;
        let incomeSources: IncomeSourceModel[] = this.component.state.incomeSources;

        if (!cardData.card) {
            return;
        }

        switch (cardData.card.description) {
            case 'new job':
                if (!incomeSources) {
                    incomeSources = await IncomeSourceService.getIncomeSources();
                }

                this.component.setState({
                    drawerState: 'new-job-goal',
                    drawerProps: {
                        card: cardData.card,
                        incomeSources,
                        handleChangeCard: this.component.incomeFactory.handleChangeCard.bind(
                            this.component.incomeFactory
                        )
                    }
                });
                break;
            case 'get promoted':
                if (!incomeSources) {
                    incomeSources = await IncomeSourceService.getIncomeSources();
                }
                this.component.setState({
                    drawerState: 'job-delta-goal',
                    drawerProps: {
                        card: cardData.card,
                        incomeSources,
                        handleChangeCard: this.component.incomeFactory.handleChangeCard.bind(
                            this.component.incomeFactory
                        )
                    }
                });
                break;
            case 'quit job':
            case 'lose job':
                if (!incomeSources) {
                    incomeSources = await IncomeSourceService.getIncomeSources();
                }

                this.component.setState({
                    drawerState: 'lose-job-goal',
                    drawerProps: {
                        card: cardData.card,
                        incomeSources,
                        handleChangeCard: this.component.incomeFactory.handleChangeCard.bind(
                            this.component.incomeFactory
                        )
                    }
                });
                break;
            case 'retire':
                incomeSources = await IncomeSourceService.getIncomeSources();
                this.component.setState({
                    drawerState: 'retire',
                    drawerProps: {
                        card: cardData.card,
                        incomeSources,
                        handleChangeCard: this.component.incomeFactory.handleChangeCard.bind(
                            this.component.incomeFactory
                        )
                    }
                });
                break;
            case 'collect social security':
                this.component.setState({
                    drawerState: 'collect-social-security-goal',
                    drawerProps: {
                        card: cardData.card,
                        handleChangeCard: this.component.incomeFactory.handleChangeCard.bind(
                            this.component.incomeFactory
                        )
                    }
                });
                break;
            case 'windfall':
            case 'win lottery':
            case 'receive inheritance':
                this.component.setState({
                    drawerState: 'windfall-goal',
                    drawerProps: {
                        card: cardData.card
                    }
                });
                break;
            case 'save for home':
                accounts = this.component.props.user.accounts;

                this.component.setState({
                    drawerState: 'save-for-home-goal',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card),
                        userAccounts: accounts,
                        onReturnToFindHomeModal: this.component.onReturnToFindHomeModal.bind(this)
                    }
                });
                break;
            case 'save for vacation':
                this.component.setState({
                    drawerState: 'save-for-vacation-goal',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'save for large purchase':
                this.component.setState({
                    drawerState: 'save-for-large-purchase',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'save for education costs':
                this.component.setState({
                    drawerState: 'save-for-education-costs',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'save for retirement':
                this.component.setState({
                    drawerState: 'save-for-retirement-goal',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'save emergency fund':
                this.component.setState({
                    drawerState: 'emergency-fund',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'save for college':
                this.component.setState({
                    drawerState: 'savings-goal',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card),
                        userAccounts: accounts
                    }
                });
                break;
            case 'save for baby':
                accounts = this.component.props.user.accounts;
                this.component.setState({
                    drawerState: 'save-for-family',
                    drawerProps: {
                        card: cardData.card,
                        handleChangeCard: this.component.incomeFactory.handleChangeCard.bind(
                            this.component.incomeFactory
                        ),
                        userAccounts: accounts
                    }
                });
                break;
            case 'buy new home':
                this.component.setState({
                    drawerState: 'buy-home-goal',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card),
                        onReturnToFindHomeModal: this.component.onReturnToFindHomeModal.bind(this)
                    }
                });
                break;
            case '2008 crash':
            case '2010 boom':
            case 'oil shock':
            case 'end of life':
                this.component.setState({
                    drawerState: 'date-goal',
                    drawerProps: {
                        card: cardData.card
                    }
                });
                break;
            case 'take a vacation':
                this.component.setState({
                    drawerState: 'take-vacation',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'make a large purchase':
                this.component.setState({
                    drawerState: 'large-purchase',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'plan for education costs':
                this.component.setState({
                    drawerState: 'plan-for-education-costs',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'donate a portion of your income':
                {
                    const offset: number = CashFlowService.getCashFlowIndexWithDate(
                        this.component.props.user.rawCashFlow,
                        cardData.card.startDate
                    );
                    const startDate = this.component.props.user.rawCashFlow[offset - 12].startDate;
                    const endDate = this.component.props.user.rawCashFlow[offset].endDate;
                    const summaries: TransactionSummary[] = await TransactionService.getTransactionSummaries(
                        startDate,
                        endDate
                    );
                    this.component.setState({
                        drawerState: 'charity-goal',
                        drawerProps: {
                            card: await CardService.getCardPayload(cardData.card),
                            summaries
                        }
                    });
                }
                break;
            case 'get sick':
            case 'wedding':
            case 'family member gets sick':
            case 'have a baby':
                this.component.setState({
                    drawerState: 'expense-goal',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
            case 'child starts college':
                this.component.setState({
                    drawerState: 'child-starts-college',
                    drawerProps: {
                        card: cardData.card
                    }
                });
                break;
            // eslint-disable-next-line no-lone-blocks
            case 'budget expenses':
                {
                    let offset: number = Math.max(
                        CashFlowService.getCashFlowIndexWithDate(
                            this.component.props.user.rawCashFlow,
                            LocalDate.now()
                        ) - 1,
                        0
                    );
                    let startDate = this.component.props.user.rawCashFlow[offset].startDate;
                    let endDate = this.component.props.user.rawCashFlow[offset].endDate;
                    const summaries: TransactionSummary[] = await TransactionService.getTransactionSummaries(
                        startDate,
                        endDate
                    );

                    offset += 1;
                    startDate = this.component.props.user.rawCashFlow[offset].startDate;
                    endDate = this.component.props.user.rawCashFlow[offset].endDate;
                    const thisMonth: TransactionSummary[] = await TransactionService.getTransactionSummaries(
                        startDate,
                        endDate
                    );

                    const categories: CategoryModel[] = await CategoryService.getCategories();

                    this.component.setState({
                        drawerState: 'budget-expenses-goal',
                        drawerProps: {
                            card: cardData.card,
                            summaries,
                            thisMonth,
                            categories,
                            startDate,
                            endDate
                        }
                    });
                }
                break;
            // eslint-disable-next-line no-lone-blocks
            case 'adjust expenses':
                {
                    const card = await CardService.getCardPayload(cardData.card);
                    this.component.setState({
                        drawerState: 'reduce-expenses-goal',
                        drawerProps: {
                            card,
                            cashFlow: this.component.props.user.cashFlow
                        }
                    });
                }
                break;
            case 'estimate life insurance':
                this.component.setState({
                    drawerState: 'life-insurance-goal',
                    drawerProps: {
                        card: cardData.card
                    }
                });
                break;
            case 'pay down debt':
                const card: CardModel = await CardService.getCardPayload(cardData.card);
                const monthly: number = _.get(card, 'attributes.monthly') || 0;

                const payDownData: PayDownData = await DebtService.getPayDownData(card.startDate.toString(), monthly);

                this.component.setState({
                    drawerState: 'pay-down-debt',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card),
                        payDownData
                    }
                });
                break;
            case 'care for a family member':
                this.component.setState({
                    drawerState: 'extended-expense',
                    drawerProps: {
                        card: await CardService.getCardPayload(cardData.card)
                    }
                });
                break;
        }
    }

    public async handleUpdateCard(
        card: CardModel = null,
        user: FullUserModel = null,
        updateDrawer: boolean = false
    ): Promise<any> {
        const drawerProps: any = this.component.state.drawerProps;
        const updatedCard: CardModel = card ? { ...card } : { ...drawerProps.card };
        const linkedCard: CardModel = await CardService.getLinkedCard(updatedCard);

        if (updatedCard) {
            updatedCard.attributes.updated = true;
            await CardService.updateCard(updatedCard, user);
            if (updateDrawer) {
                this.component.drawerFactory.updateDrawer({
                    ...this.component.state.drawerProps,
                    card: updatedCard
                });
            }
        }

        if (linkedCard) {
            await CardFactory.updateLinkedCard(updatedCard, linkedCard);
        }

        await this.component.handleReloadUser();
    }

    public async onZillowAddress(address: string, cityStateZip: string): Promise<void> {
        const card: any = this.component.state.card;
        card.attributes = card.attributes || {};
        card.attributes.zillow = await ZillowService.getPropertyByAddress(address, cityStateZip);
        card.attributes.purchase_price =
            Number(_.get(card, 'attributes.zillow.zestimate[0].amount[0]._')) || Number(card.attributes.purchase_price);
        this.component.setState({ card });
        await this.handleUpdateCard(card);

        this.component.drawerFactory.setDrawerRoute('buy-home-goal', {
            card: this.component.state.card,
            onReturnToFindHomeModal: this.component.onReturnToFindHomeModal.bind(this.component),
            handleChangeCard: this.component.cardFactory.handleChangeCard.bind(this.component.cardFactory),
            handleChangeCardAttribute: this.component.cardFactory.handleChangeCardAttribute.bind(
                this.component.cardFactory
            ),
            handleChangeCardDate: this.component.cardFactory.handleChangeCardDate.bind(this.component.cardFactory),
            handleDelete: this.component.cardFactory.handleDeleteCard.bind(this.component.cardFactory),
            handleUpdate: async () => {
                await this.handleUpdateCard();
            }
        });
    }

    public showLoader(): void {
        this.loaderTimeout = window.setTimeout(() => {
            this.component.setState({
                waitMessages: ['Updating your timeline', 'This will just take a few seconds']
            });
        }, 2500);
    }

    public hideLoader(): void {
        window.clearTimeout(this.loaderTimeout);
        this.component.setState({
            waitMessages: []
        });
        this.loaderTimeout = undefined;
    }

    public async dropCleanup(): Promise<void> {
        this.component.setState({
            goalsAdded: this.component.state.goalsAdded ? (this.component.state.goalsAdded as number) + 1 : 1
        });

        await this.component.handleReloadUser();

        this.hideLoader();

        if (
            this.component.props.user.oauth &&
            !this.component.props.user.temporary &&
            !this.component.props.user.snapshotId
        ) {
            // NOOP
        } else if (
            this.component.props.user.oauth &&
            (this.component.props.user.temporary || this.component.props.user.snapshotId)
        ) {
            if (this.component.props.goalsAdded % 4 === 0) {
                // this.component.mentorFactory.triggerBuyHomeStepFive();
                this.component.setState({ goalsAdded: 1 });
            }
        }

        this.component.setState({ dragInProgress: 0 });
    }

    public async onJaegerDrop(event: any, startDate: LocalDate, yPosition: number): Promise<void> {
        try {
            this.showLoader();
            const eventText = event.dataTransfer.getData('text');
            if (!eventText) return;
            const data: any = JSON.parse(eventText);
            const card: CardModel = await this.createCard(data, startDate, true, Math.min(yPosition, 1));
            await this.component.handleReloadUser();
            this.hideLoader();
            if (card) {
                NavigationService.goTo(this.component.props.history, `/card/${card.id}`, 'Open Card drawer').catch(
                    console.error
                );
            }
        } catch (e: any) {
            console.error('drop failed', e);
        }
    }

    public async updateCardDrawer(card: CardModel, updatedValues: Partial<CardModel>): Promise<void> {
        // if the card drawer is open, update the values in that drawer
        const drawerCard = _.get(this, 'component.state.drawerProps.card');
        if (drawerCard && drawerCard.id === card.id) {
            this.component.drawerFactory.updateDrawer({
                ...this.component.state.drawerProps,
                // include data from old drawerCard and override with new dates
                card: {
                    ...drawerCard,
                    ...updatedValues
                }
            });
        }
    }

    public async onJaegerUpdate(
        card: CardModel,
        user: FullUserModel,
        updatedValues: Partial<CardModel>
    ): Promise<void> {
        const updatedCard: CardModel = {
            ...card,
            ...updatedValues
        };
        await CardService.updateCard(updatedCard, user);
        await this.updateCardDrawer(card, updatedValues);
        await this.component.handleReloadUser();
    }

    public async onJaegerMove(card: CardModel, newDate: LocalDate): Promise<void> {
        await CardService.moveCard(card, newDate, Math.min(card.yPosition, 1));

        await this.updateCardDrawer(card, {
            yPosition: card.yPosition,
            startDate: card.startDate,
            endDate: card.endDate
        });

        await this.component.handleReloadUser();
    }

    public async onGenericDrop(event: any, dragDate: LocalDate, yPosition?: number): Promise<void> {
        if (!_.isNumber(yPosition)) yPosition = Math.random();
        event.preventDefault();
        let card: CardModel = null;

        try {
            this.showLoader();
            const eventText = event.dataTransfer.getData('text');
            const $epp: JQuery = $(event.target).parent().parent();
            if (!eventText) return;
            const data: any = JSON.parse(eventText);

            let startDate: LocalDate = dragDate;

            if (!startDate) {
                const zoomLevel: string = UnitService.getZoomLevel();
                let margin = 0;
                switch (zoomLevel) {
                    case 'months':
                        margin = 0;
                        break;
                    case 'years':
                        margin = data.isMove ? -32 : -15;
                        break;
                    case 'overall':
                        margin = data.isMove ? -37 : -16;
                        break;
                }
                startDate = UnitService.getLocalDateForDropPosition(
                    this.component.props.user,
                    (event.clientX as number) + (data.offsetX as number) + margin
                );
            }

            if (!data.isMove) {
                const card: CardModel = await this.createCard(data, startDate, true, yPosition);
                await this.component.handleReloadUser();
                this.component.goToCard(card.id);
            } else {
                if (CardFactory.isDeletionAttempt($epp)) {
                    card = CardService.getCard(data.id);

                    if (data.type === 'goal') {
                        AnimationsService.deleteCard($(`.bw-goal[data-id=${data.id}]`));
                    } else {
                        AnimationsService.deleteCard($(`.bw-event[data-id=${data.id}]`));
                    }
                    await CardService.deleteCard(data);

                    const drawerCard = _.get(this, 'component.state.drawerProps.card');
                    if (drawerCard && drawerCard.id === card.id) {
                        void NavigationService.goTo(this.component.props.history, '/', 'Top drawer');
                    }

                    this.component.setState({
                        incomeSources: null
                    });
                    await this.dropCleanup();
                    this.component.drawerFactory.handleTopDrawer();
                    return;
                }
                card = CardService.getCard(data.id);
                await CardService.moveCard(card, startDate, yPosition);

                // if the card drawer is open, update the values in that drawer
                const drawerCard = _.get(this, 'component.state.drawerProps.card');
                if (drawerCard && drawerCard.id === card.id) {
                    this.component.drawerFactory.updateDrawer({
                        ...this.component.state.drawerProps,

                        // include data from old drawerCard and override with new dates
                        card: {
                            ...drawerCard,
                            startDate: card.startDate,
                            endDate: card.endDate
                        }
                    });
                }
            }
        } finally {
            await this.dropCleanup();
        }
    }

    public addCardToUser(card: CardModel): void {
        const user: FullUserModel = { ...this.component.props.user };
        user.cards.push(card);
        this.component.props.setUser(user);
    }

    public static async addOrOpenCard(props: any, name: string): Promise<void> {
        const card: CardModel = _.find(props.user.cards, { description: name });
        if (card) {
            await NavigationService.goTo(props.drawerProps.history, `/card/${card.id}`, 'Open Card drawer');
        } else {
            const cardName: string = name.replace(/ /g, '-');
            await NavigationService.goTo(
                props.drawerProps.history,
                `/create/goal/${cardName}/1`,
                `Create Card - ${cardName}`
            );
        }
    }
}
